import type {
  Game,
  GameDetail,
  Invite,
  OpponentUser,
  WhoAmI,
} from "./types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} ${body}`.trim());
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  whoami: () => request<WhoAmI>("/api/auth/whoami"),
  requestLogin: () =>
    request<{ ok: boolean; botUsername: string; deepLink: string | null }>(
      "/api/auth/request-login",
      { method: "POST", body: "{}" },
    ),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST", body: "{}" }),
  listGames: () => request<{ games: Game[] }>("/api/games"),
  listOpponents: () => request<OpponentUser[]>("/api/games/users"),
  createGame: (body: {
    opponentUserId: number;
    creatorColor: "white" | "black" | "random";
    mode: "async" | "live";
    initialClockMs?: number;
    incrementMs?: number;
  }) =>
    request<{ game: Game }>("/api/games", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getGame: (id: number) => request<GameDetail>(`/api/games/${id}`),
  submitMove: (id: number, move: { from: string; to: string; promotion?: string }) =>
    request<{ game: Game; san: string; uci: string; ply: number; isGameOver: boolean }>(
      `/api/games/${id}/move`,
      { method: "POST", body: JSON.stringify(move) },
    ),
  resign: (id: number) =>
    request<{ game: Game }>(`/api/games/${id}/resign`, { method: "POST", body: "{}" }),
  offerDraw: (id: number) =>
    request<{ game: Game }>(`/api/games/${id}/draw/offer`, { method: "POST", body: "{}" }),
  acceptDraw: (id: number) =>
    request<{ game: Game }>(`/api/games/${id}/draw/accept`, { method: "POST", body: "{}" }),
  declineDraw: (id: number) =>
    request<{ game: Game }>(`/api/games/${id}/draw/decline`, { method: "POST", body: "{}" }),
  createInvite: () => request<Invite>("/api/invites", { method: "POST", body: "{}" }),
  listInvites: () => request<Invite[]>("/api/invites"),
  lookupInvite: (token: string) =>
    request<{
      token: string;
      valid: boolean;
      expiresAt: number;
      usedAt: number | null;
      botDeepLink: string | null;
    }>(`/api/invites/${token}`),
};
